import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Grid, TextField, Paper, Table, TableBody, TableCell, TableHead, TableRow, IconButton, MenuItem as MuiMenuItem, Menu, Button } from '@material-ui/core';
import { ArrowBack, ArrowForward, MoreVert } from '@material-ui/icons';
import AddCohort from "./AddCohort";
import CohortDetails from "./CohortDetails";
import InviteStudents from "./InviteStudents";

export default function CohortManagement({ classes }) {
    const [cohorts, setCohorts] = useState([]);
    const [error, setError] = useState(null);
    const [cohortDetailsOpen, setCohortDetailsOpen] = useState(false);
    const [selectedCohort, setSelectedCohort] = useState(null);
    const [cohortPage, setCohortPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [openAddCohort, setOpenAddCohort] = useState(false);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [selectedCohortForEmail, setSelectedCohortForEmail] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuCohort, setMenuCohort] = useState(null);

    const cohortPageSize = 10;

    useEffect(() => {
        getCohorts();
    }, []);

    const getCohorts = async () => {
        try {
            const response = await fetch(`/api/cohort`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            if (!response.ok) throw new Error('Cohort stats not found');
            const cohortData = await response.json();
            setCohorts(cohortData);
            setError(null);
        } catch (error) {
            console.error('Error fetching cohorts: ', error);
            setCohorts([]);
            setError('No cohorts found.');
        }
    };

    const filteredCohorts = useMemo(() => {
        if (!searchTerm.trim()) return cohorts;
        const term = searchTerm.toLowerCase().trim();
        return cohorts.filter(cohort => cohort.name.toLowerCase().includes(term));
    }, [cohorts, searchTerm]);

    const totalCohortPages = Math.ceil(filteredCohorts.length / cohortPageSize);
    const cohortsToShow = filteredCohorts.slice(
        (cohortPage - 1) * cohortPageSize,
        cohortPage * cohortPageSize
    );

    const handleCohortPageChange = (newPage) => setCohortPage(newPage);
    const handleCohortSearch = (event) => {
        setSearchTerm(event.target.value);
        setCohortPage(1);
    };

    const handleOpenAddCohort = () => setOpenAddCohort(true);
    const handleCloseAddCohort = () => setOpenAddCohort(false);

    const handleSaveCohort = async (newCohort) => {
        try {
            const response = await fetch(`/api/cohort`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newCohort),
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            await response.json();
            setOpenAddCohort(false);
            getCohorts();
        } catch (error) {
            console.error("Error adding cohort:", error);
        }
    };

    const handleCohortViewDetails = (cohort) => {
        setSelectedCohort(cohort);
        setCohortDetailsOpen(true);
    };

    const handleCloseCohortDetails = () => {
        setCohortDetailsOpen(false);
        setSelectedCohort(null);
    };

    const ensureInviteCode = async (cohort) => {
        if (cohort.invite_code) return cohort.invite_code;
        try {
            const res = await fetch(`/api/cohort/${cohort.id}/invite`, { method: 'GET' });
            if (!res.ok) throw new Error('Failed to fetch invite code');
            const data = await res.json();
            const code = data.invite_code;
            setCohorts(prev => prev.map(c => c.id === cohort.id ? { ...c, invite_code: code } : c));
            return code;
        } catch (e) {
            console.error('Error getting invite code', e);
            return '';
        }
    };

    const handleMenuOpen = (event, cohort) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuCohort(cohort);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuCohort(null);
    };

    const handleCopySurveyLink = async (cohort) => {
        const surveyLink = `${window.location.origin}/questions?cohortId=${cohort.id}`;
        try {
            await navigator.clipboard.writeText(surveyLink);
            alert(`Copied survey link for ${cohort.name}`);
        } catch (e) {
            alert('Failed to copy link');
        }
        handleMenuClose();
    };

    const handleEmailMembers = (cohort) => {
        setSelectedCohortForEmail(cohort);
        setEmailModalOpen(true);
        handleMenuClose();
    };

    const handleCopyInviteCode = async (cohort) => {
        const code = await ensureInviteCode(cohort);
        if (code) {
            navigator.clipboard.writeText(code);
            alert(`Copied invite code: ${code}`);
        } else {
            alert('No invite code available.');
        }
        handleMenuClose();
    };

    return (
        <div className={classes.mainContent}>
            <Typography variant="h4" align="center" gutterBottom>
                Cohort Management
            </Typography>

            {cohorts.length >= 0 && (
                <Paper className={classes.tableContainer}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="h6">Cohorts</Typography>
                        <Button variant="contained" className={classes.button} onClick={handleOpenAddCohort}>
                            Add Cohort
                        </Button>
                        <AddCohort
                            open={openAddCohort}
                            onClose={handleCloseAddCohort}
                            onSave={handleSaveCohort}
                            existingCohorts={cohorts}
                        />
                    </Box>
                    <TextField
                        label="Search cohorts by name"
                        type="text"
                        placeholder="Enter cohort name"
                        value={searchTerm}
                        onChange={handleCohortSearch}
                        fullWidth
                        className={classes.formControl}
                        variant="filled"
                    />

                    {searchTerm && (
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Showing {filteredCohorts.length} of {cohorts.length} cohorts matching "{searchTerm}"
                        </Typography>
                    )}

                    <Table className={classes.table} aria-label="cohorts table">
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell className={classes.tableCell}>Cohort Name</TableCell>
                                <TableCell className={classes.tableCell}>Start Date</TableCell>
                                <TableCell className={classes.tableCell}>End Date</TableCell>
                                <TableCell className={classes.tableCell}>Total Members</TableCell>
                                <TableCell className={classes.tableCell}>Invite Code</TableCell>
                                <TableCell className={classes.tableCell}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cohortsToShow.length > 0 ? (
                                cohortsToShow.map((cohort) => (
                                    <TableRow
                                        key={cohort.id}
                                        hover
                                        className={classes.tableRow}
                                    >
                                        <TableCell
                                            className={classes.tableCell}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleCohortViewDetails(cohort)}
                                        >
                                            {cohort.name}
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>{cohort.start_date || "—"}</TableCell>
                                        <TableCell className={classes.tableCell}>{cohort.end_date || "—"}</TableCell>
                                        <TableCell className={classes.tableCell}>{cohort.memberCount || 0}</TableCell>
                                        <TableCell className={classes.tableCell}>
                      <span
                          style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                          onClick={(e) => {
                              e.stopPropagation();
                              handleCopyInviteCode(cohort);
                          }}
                      >
                        {cohort.invite_code || '—'}
                      </span>
                                        </TableCell>
                                        <TableCell className={classes.tableCell}>
                                            <IconButton
                                                onClick={(e) => handleMenuOpen(e, cohort)}
                                                size="small"
                                            >
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        {searchTerm ? `No cohorts found matching "${searchTerm}"` : 'No cohorts available'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    {filteredCohorts.length > 0 && (
                        <div className={classes.pagination}>
                            <IconButton
                                disabled={cohortPage === 1}
                                onClick={() => handleCohortPageChange(cohortPage - 1)}
                            >
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="body1">
                                Page {cohortPage} of {totalCohortPages}
                            </Typography>
                            <IconButton
                                disabled={cohortPage === totalCohortPages || totalCohortPages === 0}
                                onClick={() => handleCohortPageChange(cohortPage + 1)}
                            >
                                <ArrowForward />
                            </IconButton>
                        </div>
                    )}
                </Paper>
            )}

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MuiMenuItem onClick={() => menuCohort && handleCohortViewDetails(menuCohort)}>
                    View Details
                </MuiMenuItem>
                <MuiMenuItem onClick={() => menuCohort && handleCopyInviteCode(menuCohort)}>
                    Copy Invite Code
                </MuiMenuItem>
                <MuiMenuItem onClick={() => menuCohort && handleCopySurveyLink(menuCohort)}>
                    Copy Survey Link
                </MuiMenuItem>
                <MuiMenuItem onClick={() => menuCohort && handleEmailMembers(menuCohort)}>
                    Email Members
                </MuiMenuItem>
            </Menu>

            {/* Modals */}
            <CohortDetails
                open={cohortDetailsOpen}
                onClose={handleCloseCohortDetails}
                cohort={selectedCohort}
            />

            <InviteStudents
                open={emailModalOpen}
                onClose={() => setEmailModalOpen(false)}
                cohort={selectedCohortForEmail}
            />
        </div>
    );
}